<template>
  <div class="doc-panel">
    <!-- 文档未就绪（二进制还没拉到）：骨架屏。
         word 文档不等审查接口，buffer 一到即渲染编辑器；
         审查进行中的视觉反馈由左栏 TaskPanel / 右栏 OpinionPanel 表达 -->
    <div v-if="!documentBuffer" class="doc-skeleton">
      <div class="doc-skeleton-page">
        <div class="ds-line ds-title"></div>
        <div class="ds-line ds-w90"></div>
        <div class="ds-line ds-w70"></div>
        <div class="ds-spacer"></div>
        <div class="ds-line ds-h"></div>
        <div class="ds-line ds-w-full"></div>
        <div class="ds-line ds-w-full"></div>
        <div class="ds-line ds-w95"></div>
        <div class="ds-line ds-w-full"></div>
        <div class="ds-line ds-w80"></div>
        <div class="ds-spacer"></div>
        <div class="ds-line ds-h"></div>
        <div class="ds-line ds-w-full"></div>
        <div class="ds-line ds-w90"></div>
        <div class="ds-line ds-w-full"></div>
        <div class="ds-line ds-w60"></div>
      </div>
    </div>

    <!-- 审查完成：编辑器 -->
    <div v-else class="editor-wrap">
      <DocxEditor
        ref="editorRef"
        class="doc-editor"
        :i18n="zhCN"
        :show-ruler="true"
        :show-outline="false"
        :document-buffer="documentBuffer"
        :document="documentBuffer ? null : documentModel"
        :document-name="contractName"
        :author="reviewer"
        :read-only="status !== 'completed'"
        :mode="editorMode"
        :show-toolbar="status === 'completed'"
        :show-menu-bar="status === 'completed'"
        :show-file-open="status === 'completed'"
        @ready="onEditorReady"
        @change="onContentChange"
      />
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onBeforeUnmount } from "vue";
import { DocxEditor, createEmptyDocument, provideLocale } from "@eigenpal/docx-editor-vue";
import { zhCN } from "@eigenpal/docx-editor-i18n";
import { extractTrackedChanges } from "@eigenpal/docx-editor-core/prosemirror/utils/extractTrackedChanges";
import {
  findParaIdRange,
  getVanillaNodeText,
} from "@eigenpal/docx-editor-core/prosemirror/paraText";
import {
  rejectChangeById,
} from "@eigenpal/docx-editor-core/prosemirror/commands";
import { saveReviewFile } from "@/api/contractReview";

provideLocale(zhCN);

const props = defineProps({
  contractName: { type: String, default: "动产买卖合同" },
  reviewer: { type: String, default: "张律师" },
  status: { type: String, default: "reviewing" },
  documentBuffer: { type: [ArrayBuffer, Uint8Array, Blob, File], default: null },
  // 审查/合同记录 ID（route.query 的 id 或 contractId），SaveRevisedFile 的 id 入参；
  // 注意不是 fileId（fileId 只用于 getFile 取文件二进制）
  id: { type: [String, Number], default: null },
  // 是否开启自动保存
  autoSave: { type: Boolean, default: true },
  // 最后一次编辑到触发保存的间隔（毫秒）
  autoSaveDelay: { type: Number, default: 60000 },
});

const emit = defineEmits(["ready", "change", "auto-save-status"]);

/* 编辑器实例 */
const editorRef = ref(null);
const documentModel = ref(createEmptyDocument());

/* 审查完成时进入修订模式（suggesting），让接受/拒绝修订生效。
   文档先于审查接口渲染（buffer 到即挂载编辑器）：审查中用 viewing 查看模式
   （库里的一等只读形态，gate PM editable），但工具栏按钮是直接 dispatch
   transaction、不走 editable 门禁（如插入图片），所以审查中同时用
   showToolbar/showMenuBar/showFileOpen 把操作入口整个隐藏，
   completed 后恢复工具栏并切到 suggesting。mode prop 库内部有 watch 同步，支持运行时切换 */
const editorMode = computed(() =>
  props.status === "completed" ? "suggesting" : "viewing"
);

const onEditorReady = () => {
  console.log("[DocPanel] editor ready");
  emit("ready");
};

/* ============ 自动保存（防抖） ============ */
const DOCX_MIME =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

let autoSaveTimer = null;
const saveInFlight = ref(false);

const pad2 = (n) => String(n).padStart(2, "0");
const formatHM = (d) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

/* 实际把编辑器内容写到后端（自动保存 / 手动保存共用）
 * 不再判 props.autoSave：调用方自己决定是否触发 */
const saveToServer = async () => {
  if (props.id == null) return;
  if (saveInFlight.value) return;
  if (!editorRef.value || typeof editorRef.value.save !== "function") return;

  saveInFlight.value = true;
  emit("auto-save-status", { state: "saving" });

  try {
    const buffer = await editorRef.value.save();
    if (!buffer) {
      emit("auto-save-status", {
        state: "error",
        error: "editor.save() returned null",
      });
      return;
    }
    const blob = new Blob([buffer], { type: DOCX_MIME });
    await saveReviewFile(props.id, blob);
    emit("auto-save-status", {
      state: "success",
      time: formatHM(new Date()),
    });
  } catch (err) {
    console.error("[DocPanel] save error:", err);
    emit("auto-save-status", { state: "error", error: err });
  } finally {
    saveInFlight.value = false;
  }
};

const doAutoSave = () => {
  if (!props.autoSave || props.id == null) return;
  return saveToServer();
};

/* 手动保存：与自动保存同一接口，仅去掉 autoSave 开关门禁 */
const manualSave = () => saveToServer();

/* 下载修改后的文件：取编辑器当前 buffer，转 blob 后浏览器下载 */
const downloadFile = async () => {
  if (!editorRef.value || typeof editorRef.value.save !== "function") {
    console.warn("[DocPanel] downloadFile: editor not ready");
    return;
  }
  try {
    const buffer = await editorRef.value.save();
    if (!buffer) {
      console.warn("[DocPanel] downloadFile: editor.save() returned null");
      return;
    }
    const blob = new Blob([buffer], { type: DOCX_MIME });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const name =
      props.contractName && props.contractName.trim()
        ? props.contractName.trim()
        : "合同";
    a.download = `${name}.docx`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  } catch (err) {
    console.error("[DocPanel] downloadFile error:", err);
  }
};

const scheduleAutoSave = () => {
  if (!props.autoSave || props.id == null) return;
  if (autoSaveTimer) clearTimeout(autoSaveTimer);
  autoSaveTimer = setTimeout(() => {
    autoSaveTimer = null;
    doAutoSave();
  }, props.autoSaveDelay);
};

const onContentChange = () => {
  emit("change");
  scheduleAutoSave();
};

onBeforeUnmount(() => {
  if (autoSaveTimer) {
    clearTimeout(autoSaveTimer);
    autoSaveTimer = null;
  }
});

/* ============ 定位原文 ============ */
/**
 * 滚动到指定段落（按 Word w14:paraId）并短暂高亮。
 * @param {string} paraId    Word w14:paraId
 * @param {Object} [options] { highlight: { color?, durationMs? } }
 * @returns {boolean}        是否成功定位（false = 编辑器未就绪 / 找不到段落）
 */
const locateByParaId = (paraId, options) => {
  if (!editorRef.value || typeof editorRef.value.scrollToParaId !== "function") {
    console.warn("[DocPanel] scrollToParaId not available on editor");
    return false;
  }
  if (paraId == null || paraId === "") {
    console.warn("[DocPanel] locateByParaId: paraId empty");
    return false;
  }
  return editorRef.value.scrollToParaId(paraId, options);
};

/* ============ 应用 / 取消修订 ============ */
/* markedVersion 里高亮标记的 class → 颜色映射（与 OpinionCard CSS 保持一致） */
const HL_CLASS_COLOR = {
  "hl-red": "D83232",
};

/* CSS 常见命名颜色 → RRGGBB（覆盖合同场景常用色） */
const NAMED_COLORS = {
  black: "000000", white: "FFFFFF", red: "FF0000", green: "008000",
  blue: "0000FF", navy: "000080", yellow: "FFFF00", orange: "FFA500",
  purple: "800080", gray: "808080", grey: "808080", brown: "A52A2A",
  pink: "FFC0CB", cyan: "00FFFF", magenta: "FF00FF", lime: "00FF00",
  teal: "008080", maroon: "800000", olive: "808000", silver: "C0C0C0",
  gold: "FFD700",
};

/* 从 style 字符串解析颜色，返回大写 RRGGBB（无 #）。
 * 支持 #hex、rgb()、CSS 命名颜色（如 blue）；解析不到返回 null */
const parseInlineColor = (style) => {
  if (!style) return null;
  const m = style.match(/color\s*:\s*([^;]+)/i);
  if (!m) return null;
  const val = m[1].trim().toLowerCase();
  const hexM = val.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hexM) {
    let hex = hexM[1];
    if (hex.length === 3) hex = hex.split("").map((c) => c + c).join("");
    return hex.toUpperCase();
  }
  const rgbM = val.match(/^rgb\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgbM) {
    return [rgbM[1], rgbM[2], rgbM[3]]
      .map((n) => parseInt(n, 10).toString(16).padStart(2, "0"))
      .join("")
      .toUpperCase();
  }
  return NAMED_COLORS[val] || null;
};

/**
 * 解析 markedVersion HTML：产出纯文本（写入文档）+ 带色片段（事后上色还原样式）。
 * 除去标签外，还记录哪些文字带颜色及其在纯文本中的偏移，供 colorInsertion 按偏移上色。
 *
 * 颜色来源（优先级）：行内 style="color:#xxx" > class 标记（hl-red）> 继承最近祖先颜色。
 * <br> → \n 保留分行；不含标签的字符串原样返回、无片段。
 *
 * 每个 segment 带 start/end：相对 plainText 的字符偏移，
 * 供 applyRevision 在插入区间内按偏移精确上色（不用再做文本搜索）。
 *
 * @returns {{ plainText: string, segments: { text: string, color: string, start: number, end: number }[] }}
 */
const parseMarkedHtml = (html) => {
  if (!html) return { plainText: "", segments: [] };
  if (!/<\w[\s\S]*>/.test(html)) return { plainText: html, segments: [] };
  const el = document.createElement("div");
  el.innerHTML = html.replace(/<br\s*\/?>/gi, "\n");

  let plainText = "";
  const segments = [];

  const resolveColor = (node, inherited) => {
    let color = inherited;
    const styleColor = parseInlineColor(node.getAttribute("style"));
    if (styleColor) color = styleColor;
    const cls = typeof node.className === "string" ? node.className : "";
    for (const c of cls.split(/\s+/)) {
      if (HL_CLASS_COLOR[c]) color = HL_CLASS_COLOR[c];
    }
    return color;
  };

  const walk = (node, inherited) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === Node.TEXT_NODE) {
        const text = child.textContent || "";
        const start = plainText.length;
        plainText += text;
        if (inherited && text.trim()) {
          segments.push({ text, color: inherited, start, end: start + text.length });
        }
      } else if (child.nodeType === Node.ELEMENT_NODE) {
        walk(child, resolveColor(child, inherited));
      }
    });
  };
  walk(el, null);
  return { plainText, segments };
};

/**
 * 把后端原文（空白 / 全半角可能与文档不一致）解析成段落里真实存在的子串。
 *
 * proposeChange 内部用 findTextInPmParagraph 做严格子串匹配，对字符敏感，
 * 直接传后端 clauseContent 常因以下差异 0 命中：
 *   - 空白（全角空格、Tab、换行）
 *   - 全角 ASCII（U+FF01–FF5E：全角逗号 ，、斜杠 ／、数字 ５、字母 Ａ 等）
 * 这里取段落 vanilla 文本（与库内部一致），双方做同样归一化后 indexOf：
 *   去全部空白 + 全角 ASCII 转半角
 * 再把命中区间映回原文带上文档的真实字符与空白，作为 proposeChange 的 search。
 *
 * 段内重复（歧义）或未命中返回 null，并打印诊断日志便于定位差异。
 */
const FW_START = 0xff01;
const FW_END = 0xff5e;
const toHalfWidth = (ch) => {
  const code = ch.charCodeAt(0);
  return code >= FW_START && code <= FW_END
    ? String.fromCharCode(code - 0xfee0)
    : ch;
};

const resolveSearchText = (doc, paraId, query) => {
  if (!query) return null;
  const range = findParaIdRange(doc, paraId);
  if (!range) {
    console.warn("[DocPanel] resolveSearchText: paraId 未定位到段落:", paraId);
    return null;
  }
  const paraNode = doc.nodeAt(range.from);
  if (!paraNode) return null;
  const vanillaText = getVanillaNodeText(paraNode);

  // 归一化段落文本：去空白 + 全角转半角，同时记录每个归一化字符在原文中的下标
  let normText = "";
  const origIndex = [];
  for (let i = 0; i < vanillaText.length; i++) {
    const ch = vanillaText[i];
    if (/\s/.test(ch)) continue;
    normText += toHalfWidth(ch);
    origIndex.push(i);
  }

  // 归一化 query（同样规则，无需保留下标）
  let normQuery = "";
  for (let i = 0; i < query.length; i++) {
    const ch = query[i];
    if (/\s/.test(ch)) continue;
    normQuery += toHalfWidth(ch);
  }
  if (!normQuery) return null;

  const hit = normText.indexOf(normQuery);
  if (hit === -1) {
    console.warn(
      "[DocPanel] resolveSearchText: 未匹配 paraId=",
      paraId,
      "\n  query(归一化):",
      normQuery,
      "\n  段落(归一化):",
      normText,
    );
    return null;
  }
  if (normText.indexOf(normQuery, hit + 1) !== -1) {
    // 段内重复 → 歧义
    console.warn(
      "[DocPanel] resolveSearchText: 段内重复匹配（歧义）paraId=",
      paraId,
      "\n  query(归一化):",
      normQuery.slice(0, 80),
    );
    return null;
  }

  const origStart = origIndex[hit];
  const origEnd = origIndex[hit + normQuery.length - 1] + 1;
  return vanillaText.slice(origStart, origEnd);
};

/**
 * 给 proposeChange 刚插入的文本按 markedVersion 的颜色上色。
 *
 * 为什么不继续用 applyFormatting({ search })：applyFormatting 内部用
 * findTextInPmParagraph（vanilla view）定位 search 文本，而 vanilla view
 * 会【排除 tracked insertion】——刚插入的新文本根本不在它的搜索范围内，
 * 于是 applyFormatting 必然返回 false、颜色永远上不去。
 *
 * 这里改成直接拿插入区间的文档位置，tr.addMark 上 textColor mark：
 * - insertion 条目：区间 = [entry.from, entry.to]
 * - replacement 条目：from 是删除起点、to 是插入终点，删除占前半段，
 *   插入区间 = [entry.from + deletedText.length, entry.to]
 * 用 doc.textBetween(区间) === plainText 兜底校验，区间算错就跳过（不破坏现状）。
 * 每个 segment 的 start/end 是相对 plainText 的偏移，加上区间起点即文档绝对位置。
 */
const colorInsertion = (view, newEntries, plainText, segments) => {
  if (!segments.length || !plainText) return;
  const schema = view.state.schema;
  if (!schema?.marks?.textColor) {
    console.warn("[DocPanel] colorInsertion: schema 无 textColor mark，跳过上色");
    return;
  }
  const doc = view.state.doc;

  // 找到本次插入对应的条目：插入区间的文本应等于 plainText
  let insFrom = null;
  for (const e of newEntries) {
    if (e.type !== "insertion" && e.type !== "replacement") continue;
    const from =
      e.type === "replacement"
        ? e.from + (e.deletedText ? e.deletedText.length : 0)
        : e.from;
    const to = e.to;
    if (doc.textBetween(from, to) === plainText) {
      insFrom = from;
      break;
    }
  }
  if (insFrom == null) {
    console.warn(
      "[DocPanel] colorInsertion: 未定位到与插入文本匹配的修订区间，跳过上色。plainText=",
      plainText.slice(0, 40),
    );
    return;
  }

  let tr = view.state.tr;
  for (const seg of segments) {
    if (seg.start == null || seg.end == null || seg.start >= seg.end) continue;
    tr = tr.addMark(
      insFrom + seg.start,
      insFrom + seg.end,
      schema.marks.textColor.create({ rgb: seg.color }),
    );
  }
  view.dispatch(tr);
};

/**
 * 在指定段落创建一条 tracked change（删除原文 + 插入新文本）。
 *
 * 两点关键处理：
 * 1. search 归一化解析——后端 clauseContent 的空白与文档不一致时，
 *    映射成段落里真实存在的子串，让 proposeChange 的严格匹配能命中。
 * 2. replaceWith 剥 HTML——修订版含 <span> 标记，去标签后再写入。
 *
 * 通过对比 proposeChange 前后的 extractTrackedChanges 结果，
 * 精确捕获本次产生的 revisionId 集合，供 cancelRevision 使用。
 *
 * @param {Object} params
 * @param {string} params.paraId        Word w14:paraId
 * @param {string} params.search        原文（被替换的文本，后端 clauseContent）
 * @param {string} params.replaceWith   新文本（修订版 markedVersion / 清洁版 cleanVersion）
 * @param {string} params.author        作者
 * @returns {Promise<{revisionIds: number[]} | null>}  失败返回 null
 */
const applyRevision = async ({ paraId, search, replaceWith, author }) => {
  if (!editorRef.value || typeof editorRef.value.proposeChange !== "function") {
    console.warn("[DocPanel] proposeChange not available on editor");
    return null;
  }
  if (!paraId) {
    console.warn("[DocPanel] applyRevision: paraId empty");
    return null;
  }
  if (!search) {
    console.warn("[DocPanel] applyRevision: search (originalContent) empty");
    return null;
  }

  const view = editorRef.value?.getEditorRef?.()?.getView?.() ?? null;
  if (!view) {
    console.warn("[DocPanel] applyRevision: editor view unavailable");
    return null;
  }

  // 1) 把后端原文解析成段落里真实存在的子串（解决空白不一致）
  const resolvedSearch = resolveSearchText(view.state.doc, paraId, search);
  if (resolvedSearch == null) {
    console.warn(
      "[DocPanel] applyRevision: search 在段落中未匹配到（含归一化尝试）:",
      String(search).slice(0, 60),
    );
    return null;
  }

  // 2) 解析 replaceWith：纯文本（写入文档）+ 带色片段（事后上色还原 markedVersion 样式）
  const { plainText: cleanReplaceWith, segments } = parseMarkedHtml(replaceWith);
  if (segments.length) {
    console.log("[DocPanel] markedVersion 带色片段:", segments);
  }

  const beforeIds = new Set(
    extractTrackedChanges(view.state).entries.map((e) => e.revisionId)
  );

  const ok = editorRef.value.proposeChange({
    paraId,
    search: resolvedSearch,
    replaceWith: cleanReplaceWith,
    author,
  });
  if (!ok) {
    console.warn("[DocPanel] proposeChange returned false");
    return null;
  }

  // proposeChange 是同步 dispatch，立刻能拿到最新 state
  const after = extractTrackedChanges(view.state).entries;
  const newEntries = after.filter((e) => !beforeIds.has(e.revisionId));
  if (newEntries.length === 0) {
    console.warn("[DocPanel] applyRevision: no new tracked change detected");
    return null;
  }

  // 3) 按 markedVersion 里的颜色给插入文本上色。
  //    newEntries 已在 colorInsertion 之前快照，故下方 revisionIds 只含文本修订；
  //    颜色 mark 附在插入文本上，取消修订 reject 插入文本时随之消失。
  if (segments.length) {
    colorInsertion(view, newEntries, cleanReplaceWith, segments);
  }

  // 收集所有相关 id：主 id + 替换场景的 insertionRevisionId + coalescedRevisionIds
  const revisionIds = new Set();
  for (const e of newEntries) {
    revisionIds.add(e.revisionId);
    if (e.insertionRevisionId != null) revisionIds.add(e.insertionRevisionId);
    (e.coalescedRevisionIds || []).forEach((id) => revisionIds.add(id));
  }
  return { revisionIds: [...revisionIds] };
};

/**
 * 取消修订：按 revisionId 逐个 reject。
 * @param {number[]} revisionIds   applyRevision 返回的 revisionIds
 * @returns {boolean}              是否成功派发
 */
const cancelRevision = (revisionIds) => {
  if (!Array.isArray(revisionIds) || revisionIds.length === 0) {
    console.warn("[DocPanel] cancelRevision: empty revisionIds");
    return false;
  }
  const view = editorRef.value?.getEditorRef?.()?.getView?.() ?? null;
  if (!view) {
    console.warn("[DocPanel] cancelRevision: editor view unavailable");
    return false;
  }
  for (const id of revisionIds) {
    rejectChangeById(id)(view.state, view.dispatch);
  }
  return true;
};

/**
 * 按条款定位并拒绝修订（文件保存后重新进入页面的场景）。
 *
 * 会话内应用的修订走 cancelRevision（手里有 revisionIds 句柄）；但保存文件再
 * 进入页面后 appliedMap 已丢失，句柄拿不到——tracked change 本身随 docx 存在
 * 文档里，这里按 paraId + 内容把对应条目重新找出来 reject：
 *   - 内容匹配：条目 deletedText ≈ originalContent（replacement/deletion 命中），
 *     或插入文本 ≈ 修订版/清洁版文本（insertion/replacement 命中）。
 *     归一化规则与 resolveSearchText 一致（去空白 + 全角转半角），并复用
 *     parseMarkedHtml 剥掉 markedVersion 的 HTML 标签；
 *   - 同段落可能有多条修订（多个意见指向同一段），必须内容匹配防误伤；
 *     匹配不上但段内只有一条文本修订 → 兜底拒绝该条；
 *     多条且均不匹配 → 无法区分，返回 0。
 *
 * @param {Object} params
 * @param {string} params.paraId          Word w14:paraId
 * @param {string} params.originalContent 被替换的原文（applyRevision 的 search）
 * @param {string[]} params.insertedTexts 应用时写入的文本候选（修订版/清洁版）
 * @returns {number} 拒绝的修订条数（0 = 段内无可匹配修订，不算失败）
 */
const cancelRevisionsInClause = ({ paraId, originalContent, insertedTexts }) => {
  if (!paraId) return 0;
  const view = editorRef.value?.getEditorRef?.()?.getView?.() ?? null;
  if (!view) {
    console.warn("[DocPanel] cancelRevisionsInClause: editor view unavailable");
    return 0;
  }
  const range = findParaIdRange(view.state.doc, paraId);
  if (!range) {
    console.warn(
      "[DocPanel] cancelRevisionsInClause: paraId 未定位到段落:",
      paraId,
    );
    return 0;
  }

  // 段内/与段重叠的文本类修订条目
  const entries = extractTrackedChanges(view.state).entries.filter(
    (e) =>
      ["insertion", "deletion", "replacement"].includes(e.type) &&
      e.from < range.to &&
      e.to > range.from,
  );
  if (entries.length === 0) return 0;

  const norm = (text) => {
    let out = "";
    const plain = text ? parseMarkedHtml(text).plainText : "";
    for (const ch of plain) {
      if (/\s/.test(ch)) continue;
      out += toHalfWidth(ch);
    }
    return out;
  };
  const normOriginal = norm(originalContent);
  const normInserted = new Set((insertedTexts || []).map(norm).filter(Boolean));

  let targets = entries.filter(
    (e) =>
      (normOriginal && e.deletedText && norm(e.deletedText) === normOriginal) ||
      (e.text && normInserted.has(norm(e.text))),
  );
  if (targets.length === 0 && entries.length === 1) {
    targets = entries;
  }
  if (targets.length === 0) {
    console.warn(
      "[DocPanel] cancelRevisionsInClause: 段内有多条修订且内容均不匹配，跳过。paraId=",
      paraId,
    );
    return 0;
  }

  const ids = new Set();
  for (const e of targets) {
    ids.add(e.revisionId);
    if (e.insertionRevisionId != null) ids.add(e.insertionRevisionId);
    (e.coalescedRevisionIds || []).forEach((id) => ids.add(id));
  }
  for (const id of ids) {
    rejectChangeById(id)(view.state, view.dispatch);
  }
  return targets.length;
};

/* 暴露编辑器方法给上层 */
defineExpose({
  editorRef,
  getEditorView: () => editorRef.value?.getEditorRef?.()?.getView?.() ?? null,
  locateByParaId,
  applyRevision,
  cancelRevision,
  cancelRevisionsInClause,
  manualSave,
  downloadFile,
});
</script>

<style lang="less" scoped>
.doc-panel {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  background: #f1f2f5;
}
.editor-wrap {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
}
.doc-editor {
  flex: 1;
  min-height: 0;
}

/* 编辑器内部滚动区背景 */
:deep(.docx-editor-scroll-container) {
  padding: 20px 0;
  background: #f0f1f3;
}

/* 追踪修订侧卡：隐藏展开后右上角的 接受(对勾)/拒绝(叉叉) 按钮。
   库的 TrackedChangeCard 未提供隐藏 prop（DocxEditor 也没有对应开关），
   该容器内只有这两个按钮，用 CSS 隐藏不影响卡片其余内容。 */
:deep(.tc-card__actions) {
  display: none;
}

/* 审查中：文档骨架屏 */
.doc-skeleton {
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 24px 0;
  background: #f0f1f3;
}
.doc-skeleton-page {
  width: 680px;
  max-width: 90%;
  margin: 0 auto;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 1px 2px rgba(31, 45, 61, 0.04);
  padding: 40px 48px 60px;
}
.ds-line {
  height: 10px;
  border-radius: 6px;
  background: #edf1f6;
  margin: 8px 0;
  animation: ds-pulse 1.5s ease-in-out infinite;
}
.ds-title {
  height: 18px;
  width: 50%;
  margin: 0 auto 24px;
}
.ds-h {
  height: 14px;
  width: 30%;
  margin: 18px 0 12px;
}
.ds-w-full {
  width: 100%;
}
.ds-w95 {
  width: 95%;
}
.ds-w90 {
  width: 90%;
}
.ds-w80 {
  width: 80%;
}
.ds-w70 {
  width: 70%;
}
.ds-w60 {
  width: 60%;
}
.ds-spacer {
  height: 18px;
}
@keyframes ds-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
</style>
